import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

// Types
export interface DiscordStatus {
    connected: boolean;
    discordUsername: string | null;
    discordAvatar: string | null;
    connectedAt: string | null;
    deliveryStatus: 'UNLINKED' | 'READY' | 'FAILED';
    deliveryReady: boolean;
    lastError: string | null;
    lastTestAt: string | null;
}

export interface DiscordLinkSession {
    code: string;
    expiresAt: string;
    installUrl: string;
}

export interface NotificationSettings {
    id: string;
    userId: string;
    // Discord settings
    discordEnabled: boolean;
    discordTaskAssigned: boolean;
    discordTaskUpdated: boolean;
    discordCommentAdded: boolean;
    discordAttachmentAdded: boolean;
    discordMention: boolean;
    discordProjectInvite: boolean;
    discordDueDateReminder: boolean;
    discordTaskOverdue: boolean;
    // E-mail fallback settings
    emailFallbackEnabled: boolean;
    emailTaskAssigned: boolean;
    emailTaskUpdated: boolean;
    emailCommentAdded: boolean;
    emailAttachmentAdded: boolean;
    emailMention: boolean;
    emailProjectInvite: boolean;
    emailDueDateReminder: boolean;
    emailTaskOverdue: boolean;
    // In-app settings
    inAppEnabled: boolean;
    inAppTaskAssigned: boolean;
    inAppTaskUpdated: boolean;
    inAppCommentAdded: boolean;
    inAppAttachmentAdded: boolean;
    inAppMention: boolean;
    inAppProjectInvite: boolean;
    inAppDueDateReminder: boolean;
    inAppTaskOverdue: boolean;
    // Reminder timing
    reminderTiming: string;
    createdAt: string;
    updatedAt: string;
}

export interface TaskReminder {
    id: string;
    taskId: string;
    userId: string;
    remindAt: string;
    reminderType: string;
    sent: boolean;
    sentAt: string | null;
    createdAt: string;
}

// Query keys
export const discordKeys = {
    all: ['discord'] as const,
    status: () => [...discordKeys.all, 'status'] as const,
    notificationSettings: () => [...discordKeys.all, 'notification-settings'] as const,
    taskReminders: (taskId: string) => [...discordKeys.all, 'reminders', taskId] as const,
};

// ============================================
// DISCORD CONNECTION
// ============================================

export function useDiscordStatus() {
    return useQuery({
        queryKey: discordKeys.status(),
        queryFn: () => apiGet<DiscordStatus>('/discord/status'),
        retry: false,
    });
}

export function useDiscordAuthUrl() {
    const { toast } = useToast();

    return useMutation({
        mutationFn: async () => {
            const response = await apiGet<{ authUrl: string }>('/discord/auth-url');
            return response.authUrl;
        },
        onSuccess: (authUrl) => {
            // Open Discord OAuth in a new window
            window.location.href = authUrl;
        },
        onError: () => {
            toast({
                title: 'Error',
                description: 'Failed to connect Discord. Please try again.',
                variant: 'destructive',
            });
        },
    });
}

export function useCreateDiscordLinkSession() {
    const { toast } = useToast();

    return useMutation({
        mutationFn: () => apiPost<DiscordLinkSession>('/discord/link-session'),
        onError: () => {
            toast({
                title: 'Discord',
                description: 'Impossible de préparer la connexion Discord.',
                variant: 'destructive',
            });
        },
    });
}

export function useDisconnectDiscord() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: () => apiDelete('/discord/disconnect'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: discordKeys.status() });
            toast({
                title: 'Discord Disconnected',
                description: 'Your Discord account has been disconnected.',
            });
        },
        onError: () => {
            toast({
                title: 'Error',
                description: 'Failed to disconnect Discord. Please try again.',
                variant: 'destructive',
            });
        },
    });
}

// ============================================
// NOTIFICATION SETTINGS
// ============================================

export function useNotificationSettings() {
    return useQuery({
        queryKey: discordKeys.notificationSettings(),
        queryFn: () => apiGet<NotificationSettings>('/notification-settings'),
    });
}

export function useUpdateNotificationSettings() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: (data: Partial<NotificationSettings>) =>
            apiPatch<NotificationSettings>('/notification-settings', data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: discordKeys.notificationSettings() });
            toast({
                title: 'Settings Updated',
                description: 'Your notification settings have been saved.',
            });
        },
        onError: () => {
            toast({
                title: 'Error',
                description: 'Failed to update settings. Please try again.',
                variant: 'destructive',
            });
        },
    });
}

// ============================================
// TASK REMINDERS
// ============================================

export function useTaskReminders(taskId: string) {
    return useQuery({
        queryKey: discordKeys.taskReminders(taskId),
        queryFn: () => apiGet<TaskReminder[]>(`/task-reminders/${taskId}`),
        enabled: !!taskId,
    });
}

export function useCreateTaskReminder() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: (data: { taskId: string; remindAt: string; reminderType: string }) =>
            apiPost<TaskReminder>('/task-reminders', data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: discordKeys.taskReminders(variables.taskId) });
            toast({
                title: 'Reminder Created',
                description: 'Your task reminder has been set.',
            });
        },
        onError: () => {
            toast({
                title: 'Error',
                description: 'Failed to create reminder. Please try again.',
                variant: 'destructive',
            });
        },
    });
}

export function useDeleteTaskReminder() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: ({ reminderId, taskId }: { reminderId: string; taskId: string }) =>
            apiDelete(`/task-reminders/${reminderId}`),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: discordKeys.taskReminders(variables.taskId) });
            toast({
                title: 'Reminder Deleted',
                description: 'Your task reminder has been removed.',
            });
        },
        onError: () => {
            toast({
                title: 'Error',
                description: 'Failed to delete reminder. Please try again.',
                variant: 'destructive',
            });
        },
    });
}

export function useAutoCreateReminders() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (taskId: string) =>
            apiPost<{ success: boolean; count: number }>('/task-reminders/auto-create', { taskId }),
        onSuccess: (_, taskId) => {
            queryClient.invalidateQueries({ queryKey: discordKeys.taskReminders(taskId) });
        },
    });
}
